/**
 * Group chat reply + soft delete (groupMessages), the data contract shared
 * with namat-mobile:
 *
 *   reply:   groupMessages.replyTo = { id, userId, userName, text (<= 120), type }
 *            type: 'text' | 'image' | 'imageWithText' | 'audio'
 *   delete:  the message doc is UPDATED (never removed) to
 *            { deleted: true, text: '', imageUrl: null, audioUrl: null, deletedAt, deletedBy }
 *   mute:    a member's own groupMembers row carries muted: boolean (read by
 *            functions/groups/messageRecipients.js; no client logic here)
 *
 * Pure, no Firebase imports, so it is tested directly (chatMessage.test.ts)
 * and can be copied as-is into namat-mobile.
 */

export type ReplyType = 'text' | 'image' | 'imageWithText' | 'audio';

export interface ReplyTo {
  id: string;
  userId: string;
  userName: string;
  /** The quoted text, single-lined and at most REPLY_TEXT_MAX code points. '' for a voice note. */
  text: string;
  type: ReplyType;
}

/** Longest quoted text stored in replyTo.text, in characters (code points). */
export const REPLY_TEXT_MAX = 120;

export interface ChatMessageLike {
  id: string;
  userId: string;
  text?: string | null;
  imageUrl?: string | null;
  audioUrl?: string | null;
  messageType?: string;
  deleted?: boolean;
}

export function isDeletedMessage(message: { deleted?: boolean } | null | undefined): boolean {
  return message?.deleted === true;
}

function truncate(text: string, max: number): string {
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  return `${chars.slice(0, max - 1).join('').trimEnd()}…`;
}

/** The kind of a message as replyTo.type records it. */
export function replyTypeOf(message: ChatMessageLike): ReplyType {
  if (message.messageType === 'audio' || message.audioUrl) return 'audio';
  const hasText = typeof message.text === 'string' && message.text.trim() !== '';
  const hasImage =
    message.messageType === 'image' || message.messageType === 'imageWithText' || Boolean(message.imageUrl);
  if (hasImage) return hasText ? 'imageWithText' : 'image';
  return 'text';
}

/**
 * The replyTo map for a reply to `original`, whose sender is shown as
 * `userName`. null for a deleted message: there is nothing left to quote.
 */
export function buildReplyTo(original: ChatMessageLike, userName: string): ReplyTo | null {
  if (isDeletedMessage(original)) return null;
  const type = replyTypeOf(original);
  const raw = typeof original.text === 'string' ? original.text.replace(/\s+/g, ' ').trim() : '';
  return {
    id: original.id,
    userId: original.userId,
    userName: userName || '',
    text: type === 'audio' ? '' : truncate(raw, REPLY_TEXT_MAX),
    type,
  };
}

/** What a quote block shows: the label to translate, and the text if any. */
export interface ReplyQuote {
  /** 'deleted' | 'voice' | 'image' choose a translated label; 'text' shows `text`. */
  kind: 'deleted' | 'voice' | 'image' | 'text';
  /** For 'image' with a caption and for 'text': the quoted text. */
  text: string;
}

/**
 * The quote for `replyTo`. `original` is the quoted message when it is loaded:
 * once it is deleted the quote says so instead of keeping the removed words.
 */
export function replyQuote(
  replyTo: Partial<ReplyTo> | null | undefined,
  original?: { deleted?: boolean } | null,
): ReplyQuote | null {
  if (!replyTo || typeof replyTo !== 'object') return null;
  if (isDeletedMessage(original)) return { kind: 'deleted', text: '' };
  const text = typeof replyTo.text === 'string' ? replyTo.text : '';
  if (replyTo.type === 'audio') return { kind: 'voice', text: '' };
  if (replyTo.type === 'image' || replyTo.type === 'imageWithText') return { kind: 'image', text };
  return { kind: 'text', text };
}

/**
 * The fields a soft delete writes. `deletedAt` is passed in (the caller's
 * serverTimestamp()) so this stays free of Firebase.
 */
export function softDeletePatch<T>(deletedBy: string, deletedAt: T) {
  return {
    deleted: true as const,
    text: '',
    imageUrl: null,
    audioUrl: null,
    deletedAt,
    deletedBy,
  };
}

/**
 * Who may delete a message: its author, or a moderator of the group (group
 * editor, creator, or a manager of the group's organization — resolved by
 * the caller). A deleted message cannot be deleted again.
 */
export function canDeleteChatMessage(
  message: { userId?: string; deleted?: boolean },
  myUserId: string | null | undefined,
  isModerator: boolean,
): boolean {
  if (!myUserId || isDeletedMessage(message)) return false;
  return message.userId === myUserId || isModerator;
}
